import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Snackbar, Alert,
} from '@mui/material';
import { gerarAcordo } from '../../../../services/core/processos/processosAcordosService';
import { isAlcadaVioladaException, formatarMensagemAlcadaViolada } from '../../../../helpers/utils/utils';
import useSessaoDialogMutacao from '../../../hooks/useSessaoDialogMutacao';

const MENSAGEM_ERRO_GENERICA = 'Não foi possível gerar o Acordo. Tente novamente.';

/**
 * Dialog "Mãozinha" (Fechar Acordo, AW2-1133 Etapa 6) — port de
 * `handleOnClickFecharAcordo` (`Acordos.jsx` real). Diferente dos demais
 * dialogs de mutação do módulo (`DialogTitulo.jsx`/`DialogExcluirTitulo.jsx`),
 * a tela real fecha a confirmação IMEDIATAMENTE ao clicar "Sim", antes do
 * `await` — o resultado (sucesso/aviso/erro) chega depois via toast
 * independente do dialog. Replicado aqui chamando `onClose()` antes do
 * `try`, com o componente sempre montado pelo pai (`open` só alterna) para
 * que o `Snackbar` de resultado sobreviva ao fechamento — mesmo mecanismo
 * que já sustenta o Snackbar de erro de `DialogExcluirTitulo.jsx` entre
 * re-aberturas.
 *
 * `gerarAcordo` tem 2 desfechos "suaves" dentro do PRÓPRIO sucesso da
 * promise (HTTP 200, não uma exceção): `codResult > 0` é uma falha de
 * negócio relatada pelo backend (`codResult === 1` = aviso, qualquer outro
 * valor = erro), `codResult === 0` é sucesso real. Exceção 422 com
 * `parametros` (alçada violada) é um 3º caminho de aviso, tratado no
 * `catch`. Os 3 desfechos são replicados separadamente — decisão explícita
 * do plano de arquitetura ("não simplificar para um único caminho de
 * sucesso/erro").
 *
 * Gate de habilitação do ícone que abre este dialog (`isFecharAcordoDesabilitado`,
 * `acordosController.js`) é só UX client-side — a autorização real de
 * transição de status do acordo permanece no backend de `gerarAcordo`, mesmo
 * padrão já aceito em todo o módulo `prototipo/`.
 */
export default function DialogFecharAcordo({
  open, acordo, idProcesso, onClose, onMutado,
}) {
  const [resultado, setResultado] = useState(null);

  const { sessaoRef, enviandoRef } = useSessaoDialogMutacao(open, idProcesso, () => {
    setResultado(null);
  });

  const onFecharResultado = () => setResultado(null);

  const onConfirmar = async () => {
    if (enviandoRef.current || !acordo) {
      return;
    }

    const sessaoAlvo = sessaoRef.current;
    const idAcordo = acordo.id;
    enviandoRef.current = true;
    onClose();

    try {
      const { data } = await gerarAcordo(idAcordo);
      const { codResult, msgResult } = data;
      if (sessaoRef.current === sessaoAlvo) {
        if (codResult > 0) {
          setResultado({ severidade: codResult === 1 ? 'warning' : 'error', mensagem: msgResult });
        } else {
          setResultado({ severidade: 'success', mensagem: 'Acordo gerado com sucesso.' });
        }
      }
    } catch (exception) {
      if (sessaoRef.current === sessaoAlvo) {
        setResultado(isAlcadaVioladaException(exception)
          ? { severidade: 'warning', mensagem: formatarMensagemAlcadaViolada(exception.response.data.parametros) }
          : { severidade: 'error', mensagem: MENSAGEM_ERRO_GENERICA });
      }
    } finally {
      if (sessaoRef.current === sessaoAlvo) {
        enviandoRef.current = false;
      }
      // Incondicional (mesmo fora da sessão alvo) — replica o `finally` de
      // `handleOnClickFecharAcordo` real, que sempre refaz a grid/cards
      // independente do desfecho. `onMutado` lê o `idProcesso` ATUAL do
      // container (nunca um valor capturado aqui), então mesmo após uma
      // troca de processo em voo o refetch aplica ao processo certo — nunca
      // ao processo antigo.
      if (onMutado) {
        onMutado();
      }
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
        <DialogTitle>Fechar Acordo</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {`Confirma o fechamento do acordo${acordo?.nroAcordo ? ` nº ${acordo.nroAcordo}` : ''}?`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Não</Button>
          <Button onClick={onConfirmar} variant="contained">Sim</Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={Boolean(resultado)}
        autoHideDuration={4000}
        onClose={onFecharResultado}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity={resultado?.severidade ?? 'info'} variant="filled" onClose={onFecharResultado}>
          {resultado?.mensagem}
        </Alert>
      </Snackbar>
    </>
  );
}

DialogFecharAcordo.propTypes = {
  open: PropTypes.bool,
  acordo: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    nroAcordo: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }),
  idProcesso: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onClose: PropTypes.func.isRequired,
  onMutado: PropTypes.func,
};

DialogFecharAcordo.defaultProps = {
  open: false,
  acordo: null,
  idProcesso: null,
  onMutado: null,
};
